import axios from 'axios'
import { signOut } from 'next-auth/react'

const API_PROXY_BASE =
  typeof window === 'undefined'
    ? `${process.env.NEXTAUTH_URL || 'http://localhost:3000'}/api/backend`
    : '/api/backend'

// Key for storing return URL in sessionStorage
const RETURN_URL_KEY = 'mengir_return_url'

// User-friendly error messages for HTTP status codes
const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Please sign in to continue.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested item could not be found.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on our end. Please try again.',
  502: 'Service temporarily unavailable. Please try again.',
  503: 'Service temporarily unavailable. Please try again.',
  504: 'Request timed out. Please try again.',
}

// Default error message
const DEFAULT_ERROR = 'Something went wrong. Please try again.'

// Create axios instance for API calls
export const apiClient = axios.create({
  baseURL: API_PROXY_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Handles session expiry by silently redirecting to login.
 * Returns a never-resolving promise to prevent error flash.
 */
async function handleSessionExpiry(): Promise<never> {
  // Store current URL for post-login redirect (exclude auth pages)
  const currentPath = window.location.pathname + window.location.search
  if (!currentPath.startsWith('/auth/')) {
    try {
      sessionStorage.setItem(RETURN_URL_KEY, currentPath)
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
  }

  // Sign out and redirect - don't await, just fire and forget
  signOut({
    callbackUrl: '/auth/login',
    redirect: true
  }).catch(() => {
    // Fallback: direct redirect if signOut fails
    window.location.href = '/auth/login'
  })

  // Return a never-resolving promise to prevent error state flash
  // The component stays in its current state until redirect completes
  return new Promise(() => {})
}

/**
 * Get and clear the stored return URL after login
 */
export function getAndClearReturnUrl(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const url = sessionStorage.getItem(RETURN_URL_KEY)
    if (url) {
      sessionStorage.removeItem(RETURN_URL_KEY)
    }
    return url
  } catch {
    return null
  }
}

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response } = error
    const status = response?.status

    // Log full error for debugging (only to console, never to user)
    console.error('API request failed:', {
      status,
      url: error.config?.url,
      data: response?.data,
    })

    // Handle 401 Unauthorized errors - silent redirect, no error flash
    if (status === 401) {
      console.warn('Session expired, redirecting to login...')

      // Check if we're in a browser environment
      if (typeof window !== 'undefined') {
        // Handle session expiry with silent redirect
        return handleSessionExpiry()
      }
    }

    // Extract specific error message from backend response if available
    // Backend sends: { error: "specific message", action: "rate_limited|auto_flagged|..." }
    const backendErrorMessage = response?.data?.error

    // Use backend message for 400/429 errors (validation, moderation, rate limiting)
    // Fall back to generic message for server errors
    let userMessage: string
    if (backendErrorMessage && (status === 400 || status === 429)) {
      userMessage = backendErrorMessage
    } else {
      userMessage = HTTP_ERROR_MESSAGES[status] || DEFAULT_ERROR
    }

    return Promise.reject(new Error(userMessage))
  }
)

export default apiClient
